import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../api/axios";
import { usePermissions } from "../../hooks/usePermissions";

export const BASE = "/api/admin/trust-ledger";

// Blank filters are dropped: the backend rejects an empty search or id.
const clean = (params = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v != null));

// ErrorBanner and toasts only need the backend's own message.
const fail = (e) => {
  throw new Error(e?.response?.data?.message || e.message);
};

export const get = (path, params) =>
  axiosInstance.get(BASE + path, { params: clean(params) }).then((r) => r.data.data).catch(fail);

export const post = (path, body) =>
  axiosInstance.post(BASE + path, body).then((r) => r.data.data).catch(fail);

// path = null leaves the query idle (e.g. no fiscal year chosen yet).
export function useLedger(path, params, opts) {
  return useQuery({
    queryKey: ["trust-ledger", path, params],
    queryFn: () => get(path, params),
    enabled: path != null,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
    ...opts,
  });
}

// Every route is role-checked by the backend as METHOD:/api/admin/trust-ledger/...
export function useLedgerCan() {
  const { can } = usePermissions();
  return (method, path) => can(`${method}:${BASE}${path}`);
}

const NONE = [];
const ALL = { pageSize: 200 };
// ponytail: one page of 200 per list; page through if a book outgrows it.
const useAll = (path) => useLedger(path, ALL, { staleTime: 60_000 }).data?.data ?? NONE;
const byId = (list) => Object.fromEntries(list.map((r) => [r._id, r]));

// Lists send ledger/heading/costCenter as bare ids; these resolve them to names.
// `baseCurrency` is the name of the currency every debit/credit is kept in
// (any active currency can be made the base); `postable` are the leaf
// accounts, the only ones a voucher line may use.
export function useLookups() {
  const options = useLedger("/options", undefined, { staleTime: Infinity }).data;
  const years = useAll("/years");
  const currencies = useAll("/currencies");
  const ledgers = useAll("/ledgers");
  const accounts = useAll("/accounts");
  const headings = useAll("/headings");
  const costCenters = useAll("/cost-centers");
  return {
    options,
    baseCurrency: options?.baseCurrency?.name ?? currencies.find((c) => c.isBase)?.name ?? "USDT",
    years,
    openYear: years.find((y) => y.status === "open"),
    currencies,
    ledgers,
    accounts,
    postable: accounts.filter((a) => !a.isGroup),
    headings,
    costCenters,
    ledger: byId(ledgers),
    heading: byId(headings),
    costCenter: byId(costCenters),
    account: byId(accounts),
  };
}

export const accountLabel = (a) => (a ? `${a.code} · ${a.name}` : "");
export const STATEMENT_LABEL = { financial_position: "Financial Position", profit_and_loss: "Profit & Loss" };

// Print and export answer files, not JSON; their errors arrive as JSON text.
export const getText = (path, params) =>
  axiosInstance
    .get(BASE + path, { params: clean(params), responseType: "text" })
    .then((r) => r.data)
    .catch((e) => {
      let message = e.message;
      try {
        message = JSON.parse(e.response.data).message || message;
      } catch {}
      throw new Error(message);
    });

export function downloadText(text, filename, type) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// These routes need the token header, so a new tab cannot open them; the HTML
// is fetched and printed from a hidden frame instead.
export const printReceipt = (id) => getText(`/receipts/${id}/print`).then(printHtml);

export function printHtml(html) {
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;width:0;height:0;border:0";
  frame.onload = () => {
    frame.contentWindow.onafterprint = () => frame.remove();
    frame.contentWindow.print();
  };
  frame.srcdoc = html;
  document.body.appendChild(frame);
}
